import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SecurityProvider } from "./context/SecurityContext";
import { SecurityGate } from "./components/security/SecurityOverlays";
import { Toaster } from "sonner";
import Login from "./pages/Login";
import InviteAccept from "./pages/InviteAccept";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import ClientDetail from "./pages/ClientDetail";
import NewSession from "./pages/NewSession";
import SessionView from "./pages/SessionView";
import Insights from "./pages/Insights";
import Compare from "./pages/Compare";
import Protocols from "./pages/Protocols";
import Community from "./pages/Community";
import CommunityProtocol from "./pages/CommunityProtocol";
import JotformIntegration from "./pages/JotformIntegration";
import Settings from "./pages/Settings";
import Disclaimer from "./pages/Disclaimer";
import AdminUsage from "./pages/AdminUsage";
import AdminProduction from "./pages/AdminProduction";
import AdminBilling from "./pages/AdminBilling";
import AdminFeedback from "./pages/AdminFeedback";
import Billing from "./pages/Billing";
import Verification from "./pages/Verification";
import BodyMapConcepts from "./pages/BodyMapConcepts";
import ClientTimeline from "./pages/ClientTimeline";
import BodyChartHistory from "./pages/BodyChartHistory";
import SessionCompare from "./pages/SessionCompare";
import ComparisonSharePage from "./pages/ComparisonSharePage";
import RecommendationsSharePage from "./pages/RecommendationsSharePage";
import ClientPortalPage from "./pages/ClientPortalPage";
import DisclaimerGateModal from "./components/DisclaimerGateModal";
import ErrorBoundary from "./components/ErrorBoundary";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-ink-500">
        <div className="animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <SecurityGate>
      <DisclaimerGateModal />
      {children}
    </SecurityGate>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SecurityProvider>
        <div data-testid="sonner-toaster">
          <Toaster position="top-right" richColors data-testid="sonner-toaster" />
        </div>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            {/* Iter Y — public client progress portal (no auth) */}
            <Route path="/p/:token" element={<ClientPortalPage />} />
            <Route path="/share/c/:token" element={<ComparisonSharePage />} />
            <Route path="/share/r/:token" element={<RecommendationsSharePage />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/insights" element={<Protected><Insights /></Protected>} />
            <Route path="/protocols" element={<Protected><Protocols /></Protected>} />
            <Route path="/community" element={<Protected><Community /></Protected>} />
            <Route path="/community/protocols/:id" element={<Protected><CommunityProtocol /></Protected>} />
            <Route path="/integrations/jotform" element={<Protected><JotformIntegration /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/admin/usage" element={<Protected><AdminUsage /></Protected>} />
            <Route path="/admin/production" element={<Protected><AdminProduction /></Protected>} />
            <Route path="/admin/billing" element={<Protected><AdminBilling /></Protected>} />
            <Route path="/admin/feedback" element={<Protected><AdminFeedback /></Protected>} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/billing/verify" element={<Protected><Verification /></Protected>} />
            <Route path="/billing/thanks" element={<Protected><Billing /></Protected>} />
            <Route path="/clients/:id" element={<Protected><ErrorBoundary><ClientDetail /></ErrorBoundary></Protected>} />
            <Route path="/clients/:clientId/timeline" element={<Protected><ClientTimeline /></Protected>} />
            <Route path="/clients/:id/body-chart" element={<Protected><BodyChartHistory /></Protected>} />
            <Route path="/clients/:id/compare" element={<Protected><SessionCompare /></Protected>} />
            <Route path="/clients/:id/compare" element={<Protected><Compare /></Protected>} />
            <Route path="/clients/:id/new-session" element={<Protected><NewSession /></Protected>} />
            <Route path="/sessions/:id" element={<Protected><SessionView /></Protected>} />
            <Route path="/design/body-concepts" element={<Protected><BodyMapConcepts /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </SecurityProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
